import React, { useEffect, useState } from 'react';
import './FamilyDetailsPage.css';
import { useLocation, useNavigate } from 'react-router-dom';
import addPersonIcon from '../../assets/addPersonIcon.jpg';
import deleteIcon from '../../assets/deleteIcon.jpg';
import editIcon from '../../assets/editIcon.jpg';
import PageHeader from './PageHeader';
import { AxiosGet } from '../http/Axios';
import ConfirmButton from '../utils/Confirm';

const FamilyDetailsPage = (props) => {
  const [familyData, setFamilyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const guid = queryParams.get('guid');

  useEffect(() => {
    document.title = 'LeapFrog Lab Registration Admin Portal';
  }, []);

  const refreshFamilyData = async () => {
    if (!guid) {
      setError('GUID not found');
      setLoading(false);
      return;
    }

    try {
      const response = await AxiosGet(
        process.env.REACT_APP_ADMIN_SERVICE + `/families/getByGuid/${guid}`
      );
      setFamilyData(response.data ?? response);
    } catch (err) {
      setError('Failed to fetch family data');
    } finally {
      setLoading(false);
    }
  };

  const handleChildDelete = async (childId) => {
    try {
      await fetch(
        process.env.REACT_APP_ADMIN_SERVICE + `/children/${childId}`,
        { method: 'DELETE' }
      );

      setLoading(true);
      setTimeout(refreshFamilyData, 500);
    } catch (error) {
      console.error('Error deleting child:', error);
    }
  };

  const handleGuardianDelete = async (guardianId) => {
    try {
      await fetch(
        process.env.REACT_APP_ADMIN_SERVICE + `/guardians/${guardianId}`,
        { method: 'DELETE' }
      );

      setLoading(true);
      setTimeout(refreshFamilyData, 500);
    } catch (error) {
      console.error('Error deleting guardian:', error);
    }
  };

  const handleFamilyDelete = async (familyId) => {
    try {
      await fetch(
        process.env.REACT_APP_ADMIN_SERVICE + `/families/familyId/${familyId}`,
        { method: 'DELETE' }
      );

      setLoading(true);
      setTimeout(() => {
        navigate(`/add-family`);
      }, 500);
    } catch (error) {
      console.error('Error deleting family:', error);
    }
  };

  useEffect(() => {
    refreshFamilyData();
  }, [guid]);

  if (loading) return <div>Loading...</div>;
  if (error) return <div>{error}</div>;
  if (!familyData) {
    console.log(familyData);
    return (
      <div className="dashboard family-details">
        <PageHeader title={`The ${familyData.familyName} Family`} />
        <div>No data found</div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const handleAddChild = () => {
    navigate(
      `/add-child?guid=${encodeURIComponent(guid)}&familyEmail=${encodeURIComponent(familyData.email)}&familyName=${encodeURIComponent(familyData.familyName)}`
    );
  };

  const handleEditChild = async (guid, familyEmail, childId) => {
    try {
      // Fetch Child data by Id
      const response = await AxiosGet(
        process.env.REACT_APP_ADMIN_SERVICE + `/children/${childId}`
      );
      const childData = response;

      // Pass the child data to the EditFamily page through the state
      navigate(`/edit-child`, {
        state: {
          guid: guid,
          familyEmail: familyEmail,
          childId: childId,
          childData: childData,
          familyName: familyData?.familyName || '',
        },
      });
    } catch (error) {
      console.error('Error fetching child data:', error);
    }
  };

  const handleAddGuardian = () => {
    navigate(
      `/add-guardian?guid=${encodeURIComponent(guid)}&familyEmail=${encodeURIComponent(familyData.email)}&familyId=${encodeURIComponent(familyData.id)}&familyName=${encodeURIComponent(familyData.familyName)}`
    );
  };

  const handleEditGuardian = async (guid, familyEmail, guardianId) => {
    try {
      // Fetch Guardian data by Id
      const response = await AxiosGet(
        process.env.REACT_APP_ADMIN_SERVICE + `/guardians/${guardianId}`
      );
      const guardianData = response;

      // Pass the guardian data to the EditFamily page through the state
      navigate(`/edit-guardian`, {
        state: {
          guid: guid,
          familyEmail: familyEmail,
          guardianId: guardianId,
          guardianData: guardianData,
          familyName: familyData?.familyName || '',
        },
      });
    } catch (error) {
      console.error('Error fetching guardian data:', error);
    }
  };

  const handleEditFamily = async (guid, familyEmail, childId) => {
    try {
      // Fetch family data by GUID
      const response = await AxiosGet(
        process.env.REACT_APP_ADMIN_SERVICE + `/families/getByGuid/${guid}`
      );
      const familyData = response;

      // Pass the family data to the EditFamily page through the state
      navigate(`/edit-family`, {
        state: {
          guid: guid,
          familyEmail: familyEmail,
          childId: childId,
          familyData: familyData,
        },
      });
    } catch (error) {
      console.error('Error fetching family data:', error);
    }
  };

  return (
    <div className="dashboard family-details">
      <PageHeader />

      <section>
        <div className="family-card">
          <h2>The {familyData.familyName} Family</h2>
          <p>
            <strong>Okay To Contact?</strong>{' '}
            {familyData.okayToContactIndicator}
          </p>
          <p>
            <strong>Referred by:</strong> {familyData.referredBy}
          </p>
          <p>
            <strong>Member since:</strong> {formatDate(familyData.createdDate)}
          </p>
          <div className="action-links">
            <a
              href="#"
              className="edit-family-link"
              onClick={(e) => {
                e.preventDefault();
                handleEditFamily(guid, familyData.email, familyData.id);
              }}
            >
              <img
                src={editIcon}
                alt="icon"
                style={{ width: '18px', height: '18px' }}
              />
              <span className="edit-family-text"> Edit</span>
            </a>
            <ConfirmButton
              type="link"
              buttonText={
                <>
                  <img
                    src={deleteIcon}
                    alt="icon"
                    style={{ width: '21px', height: '20px' }}
                  />{' '}
                  Delete
                </>
              }
              confirmTitle="Delete Family"
              confirmMessage="Are you sure you want to delete this family? This will delete all entered information."
              confirm={() => {
                handleFamilyDelete(familyData.id, guid);
              }}
            />
          </div>
        </div>
      </section>

      <section>
        <h2>
          Guardian Details &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          {familyData.guardians.length < 2 && (
                        <a href="#" className="add-guardian-link" onClick={handleAddGuardian}>
                            <img
                              src={addPersonIcon}
                              alt="icon"
                              style={{ width: '18px', height: '18px' }}
                            />
                            <span className="add-guardian-text"> Add Guardian</span>
                          </a>
          )}
        </h2>
        {(() => {
                 if (familyData.guardians.length === 1) {
                   familyData.guardians[0].isPrimaryIndicator = 'Y';
                 }

                 return familyData.guardians.length > 0 ? (
                   <div className="guardians-grid">
                       {familyData.guardians
                .slice()
                .sort((a, b) => {
                  if (a.isPrimaryIndicator === 'Y' && b.isPrimaryIndicator !== 'Y') return -1;
                  if (b.isPrimaryIndicator === 'Y' && a.isPrimaryIndicator !== 'Y') return 1;
                  return a.id - b.id;
                })
              .map((guardian, index) => (
                <div className="guardian-card" key={guardian.id}>
                <h3>Guardian {index + 1}</h3>
                <p>
                  <strong>First name:</strong> {guardian.firstName}
                </p>
                <p>
                  <strong>Last name:</strong> {guardian.lastName}
                </p>
                <p>
                  <strong>Relationship:</strong>{' '}
                  {guardian.relationshipToChild || 'N/A'}
                </p>
                <p>
                  <strong>City:</strong> {guardian.city || 'N/A'}
                </p>
                <p>
                  <strong>Zip code:</strong> {guardian.zipCode || 'N/A'}
                </p>
                <p>
                  <strong>Phone:</strong>{' '}
                  {guardian.primaryPhone || 'N/A'}
                </p>
                <p>
                  <strong>Email:</strong> {guardian.email}
                </p>
                <p>
                    <strong>Is primary? </strong> {guardian.isPrimaryIndicator}
                </p>
                <p>
                    <strong>Notes:</strong> {guardian.notes || 'N/A'}
                </p>
                <div className="action-links">
                  <a
                    href="#"
                    className="edit-guardian-link"
                    onClick={(e) => {
                      e.preventDefault();
                      handleEditGuardian(
                        guid,
                        familyData.email,
                        guardian.id,
                        guardian.isPrimaryIndicator
                      );
                    }}
                  >
                    <img
                      src={editIcon}
                      alt="icon"
                      style={{ width: '18px', height: '18px' }}
                    />
                    <span className="edit-guardian-text"> Edit</span>
                  </a>
                  {familyData.guardians.length > 1 && (
                    <ConfirmButton
                      type="link"
                      buttonText={
                        <>
                          <img
                            src={deleteIcon}
                            alt="icon"
                            style={{ width: '21px', height: '20px' }}
                          />{' '}
                          Delete
                        </>
                      }
                      confirmTitle="Delete Guardian"
                      confirmMessage="Are you sure you want to delete this guardian? This will delete all entered information."
                      confirm={() => {
                        handleGuardianDelete(guardian.id, guid);
                      }}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
                 ) : (
                          <p>No guardians found</p>
                        );
             })()}
      </section>

      <section>
        <h2>
          Child Details &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          {familyData.children.length < 5 && (
            <a href="#" className="add-child-link" onClick={handleAddChild}>
              <img
                src={addPersonIcon}
                alt="icon"
                style={{ width: '18px', height: '18px' }}
              />
              <span className="add-child-text"> Add Child</span>
            </a>
          )}
        </h2>
        <div className="children-grid">
          {familyData.children.length > 0 ? (
            familyData.children.map((child, index) => (
              <div className="child-card" key={child.id}>
                <h3>Child {index + 1}</h3>
                <p>
                  <strong>First name:</strong> {child.firstName}
                </p>
                <p>
                  <strong>Date of birth:</strong> {child.monthOfBirth + ' / ' + child.yearOfBirth}
                </p>
                <p>
                  <strong>Gender:</strong> {child.gender}
                </p>
                <p>
                  <strong>Which hand:</strong> {child.whichHandId}
                </p>
                <p>
                  <strong>Language spoken at home:</strong> {child.languageSpokenAtHome}
                </p>
                <p>
                  <strong>Additional language 1:</strong> {child.additionalLanguage1}
                </p>
                <p>
                  <strong>Additional language 2:</strong> {child.additionalLanguage2}
                </p>

                <div className="action-links">
                  <a
                    href="#"
                    className="edit-child-link"
                    onClick={(e) => {
                      e.preventDefault();
                      handleEditChild(guid, familyData.email, child.id);
                    }}
                  >
                    <img
                      src={editIcon}
                      alt="icon"
                      style={{ width: '18px', height: '18px' }}
                    />
                    <span className="edit-child-text"> Edit</span>
                  </a>
                  {familyData.children.length > 1 && (
                    <ConfirmButton
                      type="link"
                      buttonText={
                        <>
                          <img
                            src={deleteIcon}
                            alt="icon"
                            style={{ width: '21px', height: '20px' }}
                          />{' '}
                          Delete
                        </>
                      }
                      confirmTitle="Delete Child"
                      confirmMessage="Are you sure you want to delete this child? This will delete all entered information."
                      confirm={() => {
                        handleChildDelete(child.id, guid);
                      }}
                    />
                  )}
                </div>
              </div>
            ))
          ) : (
            <p>No children found</p>
          )}
        </div>
      </section>
    </div>
  );
};

export default FamilyDetailsPage;
